import React, { useEffect, useState } from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";

import { CustomPagination } from "../../customPagination";
import { ConvertISOToDate, ConvertMetersToFeet, DateHasAlreadyPassed, GetColorFromState } from "../../util";

import ResponsiveGrid from "../../grid/responsiveGrid";
import MapPreview from "../../map/mapPreview";

import { useUserAuth } from "../../contexts/authContext";
import { useMap } from "../../contexts/mapContext";
import { useNavigate } from "react-router";

export function Constraints() {
    const [showMap, setShowMap] = useState(false);
    const [constraintColumns, setConstraintColumns] = useState([]);
    const [constraintDetails, setConstraintDetails] = useState("");
    const [infoOpen, setInfoOpen] = useState(false);
    const [selectedConstraints, setSelectedConstraints] = useState([]);
    const [constraintsAreSelected, setConstraintsAreSelected] = useState(false);

    const { constraints } = useMap();
    const { user, getOrganizationByID, userOperationalStates, handleFailedFetch } = useUserAuth();

    const navigate = useNavigate();

    const DELETED_STATUS = "DELETED";
    const PLANNING_STATUS = "PLANNING";
    const ACCEPTED_STATUS = "ACCEPTED";
    const CMP_UI_OWNER = "cmp-ui-online";

    const initialColumns = [
        {
            field: "info",
            headerName: "",
            minWidth: 50,
            maxWidth: 50,
            flex: 1,
            display: "flex",
            renderCell: (cellValues) => (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                    <IconButton size="medium" onClick={(event) => handleInfoClick(event, cellValues)} sx={{ p: 0 }}>
                        <InfoIcon />
                    </IconButton>
                </Box>
            )
        },
        {
            field: "name",
            headerName: "Name",
            minWidth: 175,
            flex: 1,
            editable: false
        },
        {
            field: "state",
            headerName: "State",
            minWidth: 135,
            flex: 1,
            editable: false,
            renderCell: (cellValues) => {
                let fontColor = GetColorFromState(cellValues.formattedValue, userOperationalStates);
                return <div style={{ fontWeight: "bold", color: fontColor }}>{cellValues.formattedValue ? cellValues.formattedValue.toUpperCase() : ""}</div>;
            }
        },
        {
            field: "time_start",
            headerName: "Start",
            type: "dateTime",
            minWidth: 200,
            flex: 1,
            editable: false,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "time_end",
            headerName: "End",
            type: "dateTime",
            minWidth: 200,
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "details.volumes[0].altitude_max_agl_m",
            headerName: "Alt ft (AGL)",
            minWidth: 100,
            flex: 1,
            valueGetter: (value, row) => `${ConvertMetersToFeet(row.volumes[0].altitude_max_agl_m)}`
        },
        {
            field: "owner",
            headerName: "Owner",
            minWidth: 175,
            flex: 1,
            editable: false
        },
        {
            field: "type",
            headerName: "Type",
            minWidth: 175,
            flex: 1,
            editable: false
        },
        {
            field: "organization_id",
            headerName: "Org",
            editable: false,
            minWidth: 100,
            flex: 1,
            renderCell: (cellValues) => {
                const org = getOrganizationByID(cellValues.formattedValue);
                if (org != undefined) {
                    return <div>{org.name}</div>;
                }
            }
        }
    ];

    const managerColumns = [
        {
            field: "status",
            headerName: "",
            editable: false,
            minWidth: 100,
            flex: 1,
            display: "flex",
            renderCell: (cellValues) => {
                if ((user.user_role_id == 4 || user.user_role_id == 0) && cellValues.row.owner == CMP_UI_OWNER) {
                    if (cellValues.row.state && cellValues.row.state.toUpperCase() == PLANNING_STATUS) {
                        return <Button onClick={(event) => handleStatusClick(event, cellValues)}>Publish</Button>;
                    }
                }
            }
        },
        {
            field: "Cancel",
            align: "center",
            headerAlign: "center",
            minWidth: 75,
            flex: 1,
            sortable: false,
            display: "flex",
            renderCell: (cellValues) => {
                if ((user.user_role_id == 4 || user.user_role_id == 0) && cellValues.row.owner == CMP_UI_OWNER)
                    return (
                        <IconButton onClick={(event) => handleCancelClick(event, cellValues)} id={`cancel-${cellValues.row.volumeId}`}>
                            <CancelIcon />
                        </IconButton>
                    );
            }
        }
    ];

    useEffect(() => {
        setupColumnsFromLogin();
    }, []);

    useEffect(() => {
        if (constraintsAreSelected === false) {
            setSelectedConstraints(constraints);
        }
    }, [constraints, selectedConstraints, constraintsAreSelected]);

    const setupColumnsFromLogin = () => {
        const columns = initialColumns;
        if (user.user_role != "Operator") {
            columns.push(...managerColumns);
        }
        setConstraintColumns(columns);
    };

    const handleCancelClick = (event, cellValues) => {
        event.stopPropagation();
        if (!confirm("Are you sure you want to cancel this constraint?")) {
            return;
        }
        const constraint = cellValues.row;
        constraint.state = DELETED_STATUS;

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(constraint)
        };
        fetch("/api/constraint/delete", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .catch((err) => handleFailedFetch(err));
    };

    const handleStatusClick = (event, cellValues) => {
        event.stopPropagation();

        if (DateHasAlreadyPassed(cellValues.row.time_start)) {
            return alert(
                "The start time of this constraint has passed. If you wish to update it, please visit the Map and right-click the constraint to edit."
            );
        } else if (!confirm("Are you sure you want to publish this constraint?")) {
            return;
        }

        const constraint = cellValues.row;
        constraint.state = ACCEPTED_STATUS;
        constraint.version = parseInt(constraint.version) + 1;

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(constraint)
        };
        fetch("/api/constraint/update", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .catch((err) => handleFailedFetch(err));
    };

    const handleInfoClick = (event, cellValues) => {
        event.stopPropagation();
        const displayDetails = {
            name: cellValues.row.name,
            uuid: cellValues.row.constraint_uuid,
            maxAlt: ConvertMetersToFeet(cellValues.row.volumes[0].altitude_max_agl_m)
        };
        setConstraintDetails(displayDetails);
        setInfoOpen(true);
    };

    const handleCloseInfo = () => {
        setInfoOpen(false);
    };

    const handleCheckboxSelected = (ids) => {
        if (ids.length > 0 && showMap === false) {
            setShowMap(true);
        }
        const selected = constraints.filter(({ constraint_uuid }) => {
            return ids.includes(constraint_uuid);
        });
        if (selected.length > 0) {
            setConstraintsAreSelected(true);
            setSelectedConstraints(selected);
        } else {
            setConstraintsAreSelected(false);
            setSelectedConstraints(constraints);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "column" },
                width: "100%",
                height: "100%",
                justifyContent: { xs: "flex-end", sm: "flex-start" }
            }}
        >
            <Paper
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: { xs: "column-reverse", sm: "column" },
                    gap: { xs: 1, sm: 2 },
                    p: { xs: 1, sm: 2 },
                    borderTopRightRadius: 0,
                    botderTopLeftRadius: 0,
                    height: { xs: "100%", sm: "578px" },
                    flexShrink: { xs: "unset", sm: 0 }
                }}
            >
                <Box sx={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                    <ResponsiveGrid
                        primaryColumns={["name", "time_start"]}
                        checkboxSelection
                        rows={constraints}
                        disableVirtualization
                        disableRowSelectionOnClick
                        columns={constraintColumns}
                        getRowId={(row) => row.constraint_uuid}
                        pagination
                        slots={{ pagination: CustomPagination }}
                        onRowSelectionModelChange={handleCheckboxSelected}
                        sx={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
                    />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        data-testid="largePreview"
                        sx={{ mr: "auto" }}
                        onClick={() => setShowMap((prev) => !prev)}
                    >
                        {showMap ? "Hide Map" : "Preview on Map"}
                    </Button>
                    {user.user_role === "Admin" || user.user_role === "Airspace Manager" ? (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            data-testid="create"
                            id="createConstraint"
                            onClick={() => navigate("constraints")}
                        >
                            Create on Map
                        </Button>
                    ) : (
                        <></>
                    )}
                </Box>
            </Paper>

            {showMap === true ? (
                <Paper sx={{ flexShrink: 0, height: { xs: "40%", sm: "400px" }, px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 0 }, pb: { xs: 0, sm: 2 } }}>
                    <MapPreview selected_constraints={selectedConstraints} />
                </Paper>
            ) : (
                <></>
            )}

            <Dialog open={infoOpen} onClose={handleCloseInfo}>
                <DialogContent sx={{ minWidth: "350px", pb: 0 }}>
                    <Box sx={{ minWidth: 350, marginTop: "5px" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ display: "flex", justifyContent: "space-between" }}>
                            <b>Constraint: </b>
                            <span>{constraintDetails.name}</span>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: "flex", justifyContent: "space-between" }}>
                            <b>UUID: </b>
                            <span>{constraintDetails.uuid}</span>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: "flex", justifyContent: "space-between" }}>
                            <b>Max Altitude: </b>
                            <span>{constraintDetails.maxAlt}ft</span>
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseInfo} sx={{ m: "auto" }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
